import React, { useEffect, useState } from 'react';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { canShowPurchaseCta } from '../utils/storeCompliance';

const CUISINE_EMOJI = {
  italien: '🍝',
  japonais: '🍣',
  francais: '🥖',
  brasserie: '🍻',
  asiatique: '🥢',
  libanais: '🥙',
  vegetarien: '🥗',
  restaurant: '🍴',
  autre: '🍴',
};
function emojiForCuisine(type) {
  return CUISINE_EMOJI[type] || '🍴';
}

export default function RestaurantSwitcher() {
  const {
    restaurants,
    currentRestaurantId,
    quotaReached,
    isSwitcherOpen,
    closeSwitcher,
    setCurrentRestaurant,
    openAdd,
  } = useCurrentRestaurant();

  // Anim slide-up : on attend 1 frame avant de basculer translateY 100%→0 pour
  // que la transition s'applique (sinon le node nait directement à 0).
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (isSwitcherOpen) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    return undefined;
  }, [isSwitcherOpen]);

  // Lock body scroll quand ouvert
  useEffect(() => {
    if (!isSwitcherOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeSwitcher(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isSwitcherOpen, closeSwitcher]);

  if (!isSwitcherOpen) return null;

  const handleSelect = (id) => {
    if ('vibrate' in navigator) navigator.vibrate(5);
    setCurrentRestaurant(id);
    // Petite latence pour laisser l'animation de la cocheck finir avant close.
    setTimeout(() => closeSwitcher(), 250);
  };

  const handleAdd = () => {
    if (quotaReached) {
      // V1 : log + redirect plus tard. Pour cette PR on log (page abonnement
      // mobile pas encore prête côté navigation).
      console.log('[RestaurantSwitcher] Quota atteint — passer à Intensif');
      // Laisser l'utilisateur fermer manuellement pour qu'il voie l'état.
      return;
    }
    closeSwitcher();
    setTimeout(() => openAdd(), 250);
  };

  return (
    <div style={styles.overlay} onClick={closeSwitcher} role="dialog" aria-modal="true">
      <div
        style={{ ...styles.sheet, transform: entered ? 'translateY(0)' : 'translateY(100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.handle} aria-hidden />

        <h2 style={styles.title}>Mes restaurants</h2>

        <ul style={styles.list}>
          {restaurants.length === 0 && (
            <li style={styles.empty}>Aucun restaurant.</li>
          )}
          {restaurants.map((r, idx) => {
            const isCurrent = r.id === currentRestaurantId;
            const isLast = idx === restaurants.length - 1;
            return (
              <li key={r.id} style={isLast ? styles.itemLast : styles.item}>
                <button
                  type="button"
                  onClick={() => handleSelect(r.id)}
                  style={styles.itemBtn}
                  aria-current={isCurrent ? 'true' : undefined}
                >
                  <span style={styles.emoji} aria-hidden>{emojiForCuisine(r.type_cuisine)}</span>
                  <span style={styles.body}>
                    <span style={styles.name}>{r.nom}</span>
                    <span style={styles.meta}>
                      {[r.ville, r.type_cuisine].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </span>
                  {isCurrent && <span style={styles.check} aria-hidden>✓</span>}
                </button>
              </li>
            );
          })}
        </ul>

        {quotaReached ? (
          <button type="button" onClick={handleAdd} style={styles.lockBtn}>
            {/* ⚠️ Un prix affiché pour inciter à monter de formule est un
                appel à l'achat : interdit dans l'app (App Store 3.1.3(f)). */}
            {canShowPurchaseCta()
              ? '🔒 Passer à Intensif (179€/mois)'
              : '🔒 Limite d’établissements atteinte'}
          </button>
        ) : (
          <button type="button" onClick={handleAdd} style={styles.addBtn}>
            + Ajouter un restaurant
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-end',
  },
  sheet: {
    width: '100%',
    background: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: '12px 20px calc(env(safe-area-inset-bottom) + 24px) 20px',
    transition: 'transform 250ms cubic-bezier(0.32, 0.72, 0, 1)',
    transform: 'translateY(100%)',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -8px 30px rgba(0, 0, 0, 0.18)',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: '#EDEAE3',
    margin: '0 auto 16px',
  },
  title: {
    margin: '0 0 12px',
    fontSize: 18,
    fontWeight: 700,
    color: '#1A1A1A',
    letterSpacing: '-0.01em',
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    overflowY: 'auto',
    flex: '0 1 auto',
  },
  empty: {
    padding: '16px 4px',
    fontSize: 13,
    color: '#9A9A9A',
    textAlign: 'center',
  },
  item: {
    borderBottom: '1px solid #EDEAE3',
  },
  itemLast: {
    borderBottom: 'none',
  },
  itemBtn: {
    width: '100%',
    minHeight: 56,
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
  },
  emoji: {
    width: 32,
    fontSize: 22,
    lineHeight: 1,
    flexShrink: 0,
    textAlign: 'center',
  },
  body: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  name: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1A1A1A',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontSize: 13,
    color: '#6B6B6B',
  },
  check: {
    color: '#10B981',
    fontSize: 18,
    fontWeight: 700,
    paddingLeft: 8,
    flexShrink: 0,
  },
  addBtn: {
    marginTop: 16,
    height: 52,
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  lockBtn: {
    marginTop: 16,
    height: 52,
    background: '#1A1A1A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
